/**
 * Ready to Bill — AR queue model for the Billing & Payables hub.
 *
 * The queue is derived live from progress sales orders + milestone-anchored
 * due forecasts (`paymentDueFlags`), then merged with manually tracked
 * `billingReadyItems` docs. Marking an item "ready" creates a doc that alerts
 * the payables team (Cloud Function fan-out); the EA then advances its status
 * as invoices are created/sent/paid in QuickBooks.
 *
 * Pure module — no firebase imports (unit-testable). Firestore operations
 * live in `readyToBillService.ts`.
 */

import type {
  ChangeOrder,
  EnhancedDeal,
  Milestone,
  ProjectTask,
  QBInvoice,
  SalesOrder,
  SalesOrderInstallmentInvoice,
  TaskGroup,
  TimberProject,
} from '../types';
import {
  buildPaymentScheduleBreakdown,
  changeOrderBillsImmediately,
  effectiveBilledInstallmentIds,
  invoicedInstallmentLocks,
  isEffectiveProgressBilling,
  legacyDepositSatisfiedFromSalesOrder,
  legacyFullPaymentSatisfiedFromSalesOrder,
  type PaymentInstallmentId,
} from './paymentSchedule';
import {
  computePassedInstallments,
  computePaymentDueForecast,
  describeReceivableDueAnchor,
  pickTimberProjectForDealSchedule,
  resolvePaymentDueTaskAnchors,
  toDateSafe,
  type MilestoneLike,
  type PaymentLagSettings,
} from './paymentDueFlags';
import { getProjectDisplayName } from './projectName';
import { invoiceClosesMilestone } from './partialInvoiceAllocation';

export type BillingReadyStatus = 'ready' | 'invoiced' | 'sent' | 'paid' | 'dismissed';

export const BILLING_READY_STATUS_LABELS: Record<BillingReadyStatus, string> = {
  ready: 'Ready to bill',
  invoiced: 'Invoiced in QB',
  sent: 'Sent to client',
  paid: 'Paid',
  dismissed: 'Dismissed',
};

/** One step earlier. Ready to bill returns to the live forecast. */
export function previousBillingReadyStatus(
  status: BillingReadyStatus,
): BillingReadyStatus | 'forecast' | null {
  switch (status) {
    case 'ready':
      return 'forecast';
    case 'invoiced':
      return 'ready';
    case 'sent':
      return 'invoiced';
    case 'paid':
      return 'sent';
    default:
      return null;
  }
}

export interface BillingReadyItem {
  id?: string;
  dealId: string;
  salesOrderId?: string;
  /** 'deposit' | 'pre_shipment' | 'final' for installments; `co-{id}` for change orders. */
  installmentId?: string;
  kind: 'installment' | 'change_order' | 'custom';
  label: string;
  detail?: string;
  amount: number;
  status: BillingReadyStatus;
  notes?: string;
  quickbooksInvoiceNum?: string;
  markedReadyByUserId: string;
  markedReadyByUserName: string;
  markedReadyAt: Date;
  updatedByUserId?: string;
  updatedByUserName?: string;
  updatedAt?: Date;
}

export interface ReadyToBillRow {
  /** Stable key — `${dealId}:${installmentId}` or `co:${dealId}:${coId}`. */
  key: string;
  kind: 'installment' | 'change_order';
  dealId: string;
  salesOrderId?: string;
  installmentId?: PaymentInstallmentId;
  changeOrderId?: string;
  coReference?: string;
  dealLabel: string;
  /** Timber project the installment is scheduled against. */
  projectId?: string;
  /** Live schedule task the due date tracks, when one exists. */
  anchorTaskId?: string;
  /** '50%' / '40%' / '10%' or CO reference. */
  label: string;
  detail: string;
  amount: number;
  dueDate: Date | null;
  /**
   * Why the due date is that day — e.g. "7 business days after Production
   * ends Oct 7, 2026". Built from the same task/milestone anchors as the
   * Gantt flags and Billing & Payables page.
   */
  dueAnchor?: string;
  estimated: boolean;
  billableNow: boolean;
  tracked?: BillingReadyItem;
  /** Set when a synced QB invoice is fully paid (even without a billingReadyItems doc). */
  qbPaid?: boolean;
  /** Set when a synced QB invoice exists but is not fully paid yet. */
  qbInvoiced?: boolean;
  quickbooksInvoiceNum?: string;
  /** QuickBooks invoice id when this row is one slice of a milestone. */
  quickbooksInvoiceId?: string;
  /** Full milestone amount when `amount` is only the unbilled remainder. */
  scheduleAmount?: number;
}

export interface ReadyToBillInputs {
  /** Base (non-change-order) sales orders. */
  salesOrders: Array<SalesOrder & { id: string }>;
  /** Change-order sales orders (`co-{changeOrderId}`). */
  coSalesOrders?: Array<SalesOrder & { id: string }>;
  /** QuickBooks invoice mirror — used to detect paid CO / installment invoices. */
  qbInvoices?: Array<QBInvoice & { id: string }>;
  deals: EnhancedDeal[];
  timberProjects: TimberProject[];
  milestones: Milestone[];
  changeOrders: ChangeOrder[];
  trackedItems: BillingReadyItem[];
  /** When set, 40% due dates follow the last Production-group task end. */
  projectTasks?: ProjectTask[];
  taskGroups?: TaskGroup[];
  /** Business-day lag per installment (financeSettings/paymentTerms); defaults 5/7/7. */
  paymentLagSettings?: PaymentLagSettings;
  now?: Date;
  /** Include every progress installment for project summary cards (ignore legacy-full skip). */
  forProjectSummary?: boolean;
}

/** Firebase auto-ids look like this — never show them as a project name. */
function looksLikeDocumentId(value: string): boolean {
  return /^[A-Za-z0-9]{16,}$/.test(value);
}

function moneyInDealLabel(
  deal: EnhancedDeal | undefined,
  so: { clientName?: string },
  project: { name?: string; clientName?: string } | undefined,
  dealId: string,
): string {
  const fromDeal = deal
    ? (getProjectDisplayName(deal) || deal.title || '').trim()
    : '';
  if (fromDeal && !looksLikeDocumentId(fromDeal)) return fromDeal;
  const fromProject = (project?.name || project?.clientName || '').trim();
  if (fromProject && !looksLikeDocumentId(fromProject)) return fromProject;
  const fromSo = (so.clientName || '').trim();
  if (fromSo) return fromSo;
  return fromDeal || dealId;
}

function coDisplayReference(co: ChangeOrder, deal?: EnhancedDeal): string {
  if (co.reference) return co.reference;
  const n = `CO-${String(Math.max(1, Math.floor(Number(co.number) || 1))).padStart(3, '0')}`;
  const pn = (deal?.projectNumber || '').trim();
  return pn ? `${pn}-${n}` : n;
}

function formatCoAdjustmentsNote(
  adjustments: Array<{ reference: string; amount: number }>,
): string {
  if (!adjustments.length) return '';
  return adjustments
    .map((a) => {
      const formatted = Math.abs(a.amount).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${a.amount >= 0 ? '+' : '−'}${formatted} ${a.reference}`;
    })
    .join(', ');
}

function coTrackedKey(dealId: string, coId: string): string {
  return `co:${dealId}:${coId}`;
}

function parseCoIdFromTrackedItem(item: BillingReadyItem): string | undefined {
  if (item.installmentId?.startsWith('co-')) return item.installmentId.slice(3);
  return undefined;
}

/** When a deal has multiple base sales orders, keep the one that drives billing. */
export function pickPrimaryBaseSalesOrders(
  orders: Array<SalesOrder & { id: string }>,
): Array<SalesOrder & { id: string }> {
  const byDeal = new Map<string, Array<SalesOrder & { id: string }>>();
  for (const so of orders) {
    if (so.isChangeOrder || !so.dealId) continue;
    const list = byDeal.get(so.dealId) ?? [];
    list.push(so);
    byDeal.set(so.dealId, list);
  }

  const score = (so: SalesOrder & { id: string }): number => {
    let s = 0;
    if (so.billingStyle === 'progress') s += 1_000_000;
    if (isEffectiveProgressBilling(so)) s += 500_000;
    const installments = (so.installmentInvoices ?? []).filter((e) => e.status !== 'voided').length;
    s += installments * 10_000;
    if (so.quickbooksInvoiceId) s += 5_000;
    s += Math.round(Number(so.grandTotal) || 0);
    const created = so.createdAt instanceof Date ? so.createdAt.getTime() : 0;
    s += created / 1_000_000_000;
    return s;
  };

  const picked: Array<SalesOrder & { id: string }> = [];
  Array.from(byDeal.values()).forEach((list) => {
    picked.push(
      list.slice().sort((a: SalesOrder & { id: string }, b: SalesOrder & { id: string }) => score(b) - score(a))[0]!,
    );
  });
  return picked;
}

function normalizeQbInvoiceId(id: string | undefined): string {
  if (!id) return '';
  return id.startsWith('qb_') ? id.slice(3) : id;
}

function normalizeInvoiceNumber(num: string | undefined): string {
  return (num || '').trim().replace(/^#/, '');
}

function buildQbInvoiceIndexes(
  invoices: Array<QBInvoice & { id: string }> | undefined,
): {
  byId: Map<string, QBInvoice & { id: string }>;
  byNumber: Map<string, QBInvoice & { id: string }>;
} {
  const byId = new Map<string, QBInvoice & { id: string }>();
  const byNumber = new Map<string, QBInvoice & { id: string }>();
  for (const inv of invoices ?? []) {
    if (inv.quickbooksInvoiceId) {
      byId.set(inv.quickbooksInvoiceId, inv);
      const stripped = normalizeQbInvoiceId(inv.quickbooksInvoiceId);
      if (stripped && stripped !== inv.quickbooksInvoiceId) byId.set(stripped, inv);
    }
    const num = normalizeInvoiceNumber(inv.invoiceNumber);
    if (num) byNumber.set(num, inv);
  }
  return { byId, byNumber };
}

function isQbInvoicePaid(inv: QBInvoice | undefined): boolean {
  if (!inv) return false;
  if (inv.status === 'paid' || inv.status === 'voided') return inv.status === 'paid';
  const balance = Number(inv.balance);
  const total = Number(inv.totalAmount);
  if (Number.isFinite(balance) && balance <= 0) return total > 0 || balance === 0;
  return false;
}

function isQbInvoiceOpen(inv: QBInvoice | undefined): boolean {
  if (!inv) return false;
  if (inv.status === 'voided' || inv.status === 'removed_in_qb') return false;
  return !isQbInvoicePaid(inv);
}

function isInstallmentRecordPaid(
  entry?: Pick<SalesOrderInstallmentInvoice, 'status' | 'balance'> | null,
): boolean {
  if (!entry) return false;
  if (entry.status === 'paid') return true;
  if (entry.status === 'voided') return false;
  const balance = Number(entry.balance);
  return Number.isFinite(balance) && balance <= 0;
}

function findQbInvoice(
  invoiceId: string | undefined,
  invoiceNum: string | undefined,
  indexes: ReturnType<typeof buildQbInvoiceIndexes>,
): (QBInvoice & { id: string }) | undefined {
  if (invoiceId) {
    const direct = indexes.byId.get(invoiceId) ?? indexes.byId.get(normalizeQbInvoiceId(invoiceId));
    if (direct) return direct;
  }
  const num = normalizeInvoiceNumber(invoiceNum);
  return num ? indexes.byNumber.get(num) : undefined;
}

function getQbPaymentFlags(
  invoiceId: string | undefined,
  indexes: ReturnType<typeof buildQbInvoiceIndexes>,
  installment?: Partial<Pick<
    SalesOrderInstallmentInvoice,
    'status' | 'balance' | 'quickbooksInvoiceNum'
  >> | null,
): { qbPaid?: boolean; qbInvoiced?: boolean } {
  if (isInstallmentRecordPaid(installment)) return { qbPaid: true };
  if (installment?.status === 'voided') return {};
  const inv = findQbInvoice(invoiceId, installment?.quickbooksInvoiceNum, indexes);
  if (isQbInvoicePaid(inv)) return { qbPaid: true };
  if (isQbInvoiceOpen(inv) || invoiceId) return { qbInvoiced: true };
  return {};
}

/**
 * Immediate COs can get more than one sales order (double-submit). Prefer any
 * sibling whose QuickBooks invoice is paid so a leftover open duplicate does
 * not keep the change order in forecast AR.
 */
function matchingChangeOrderSalesOrders(
  co: Pick<ChangeOrder, 'id' | 'salesOrderId'>,
  orders: Array<SalesOrder & { id: string }>,
): Array<SalesOrder & { id: string }> {
  const wantedIds = new Set<string>();
  if (co.salesOrderId) wantedIds.add(co.salesOrderId);
  wantedIds.add(`co-${co.id}`);
  return orders.filter(
    (so) => so.changeOrderId === co.id || wantedIds.has(so.id),
  );
}

function salesOrderQbFlags(
  so: Pick<SalesOrder, 'quickbooksInvoiceId' | 'quickbooksInvoiceNum'> | undefined,
  indexes: ReturnType<typeof buildQbInvoiceIndexes>,
): { qbPaid?: boolean; qbInvoiced?: boolean } {
  if (!so) return {};
  return getQbPaymentFlags(so.quickbooksInvoiceId, indexes, {
    quickbooksInvoiceNum: so.quickbooksInvoiceNum,
  });
}

function preferPaidChangeOrderSalesOrder(
  matches: Array<SalesOrder & { id: string }>,
  indexes: ReturnType<typeof buildQbInvoiceIndexes>,
): {
  preferred?: SalesOrder & { id: string };
  qbPaid?: boolean;
  qbInvoiced?: boolean;
} {
  if (matches.length === 0) return {};
  let paid: (SalesOrder & { id: string }) | undefined;
  let invoiced: (SalesOrder & { id: string }) | undefined;
  for (const so of matches) {
    const flags = salesOrderQbFlags(so, indexes);
    if (flags.qbPaid) {
      paid = paid ?? so;
    } else if (flags.qbInvoiced) {
      invoiced = invoiced ?? so;
    }
  }
  if (paid) return { preferred: paid, qbPaid: true };
  if (invoiced) return { preferred: invoiced, qbInvoiced: true };
  return { preferred: matches[0] };
}

/**
 * PM / Finance has recorded the approval gate. A QuickBooks invoice by itself
 * does not count — the invoice can exist before anyone approves it for sending.
 */
export function invoiceFinanceApproved(
  row: Pick<ReadyToBillRow, 'tracked'>,
): boolean {
  const tracked = row.tracked?.status;
  return tracked === 'ready' || tracked === 'invoiced' || tracked === 'sent' || tracked === 'paid';
}

/**
 * Invoice lifecycle index: Forecast, Ready to bill, Invoiced in QB, Sent to client, Paid.
 * An invoice created in QuickBooks stays on Ready to bill until PM / Finance approves it.
 */
export function invoiceWorkflowStage(
  row: Pick<ReadyToBillRow, 'tracked' | 'qbPaid' | 'qbInvoiced'>,
): number | null {
  const tracked = row.tracked?.status;
  if (tracked === 'dismissed') return null;
  if (row.qbPaid || tracked === 'paid') return 4;
  if (tracked === 'sent') return 3;
  if (!invoiceFinanceApproved(row)) return row.qbInvoiced ? 1 : 0;
  if (row.qbInvoiced || tracked === 'invoiced') return 2;
  if (tracked === 'ready') return 1;
  return 0;
}

/** CRM-marked paid/dismissed, or a synced QuickBooks paid flag. */
export function isReceivableSettled(
  row: Pick<ReadyToBillRow, 'qbPaid' | 'tracked'>,
): boolean {
  if (row.qbPaid) return true;
  const tracked = row.tracked?.status;
  return tracked === 'paid' || tracked === 'dismissed';
}

/** Hide rows that are paid/dismissed in workflow or synced as paid/invoiced in QuickBooks. */
export function shouldHideReadyToBillRow(row: ReadyToBillRow, showCompleted: boolean): boolean {
  if (showCompleted) {
    return row.tracked?.status === 'dismissed';
  }
  if (row.qbPaid || row.qbInvoiced) return true;
  const tracked = row.tracked?.status;
  return tracked === 'paid' || tracked === 'dismissed';
}

const INSTALLMENT_BILLING_ORDER: readonly PaymentInstallmentId[] = [
  'deposit',
  'pre_shipment',
  'final',
];

export function installmentBillingSortIndex(
  installmentId?: PaymentInstallmentId,
): number {
  if (!installmentId) return INSTALLMENT_BILLING_ORDER.length;
  const idx = INSTALLMENT_BILLING_ORDER.indexOf(installmentId);
  return idx === -1 ? INSTALLMENT_BILLING_ORDER.length : idx;
}

/** Client billing display order — 50% deposit, then 40%, then 10%, then change orders. */
export function compareReadyToBillBillingChronology(
  a: ReadyToBillRow,
  b: ReadyToBillRow,
): number {
  if (a.kind === 'installment' && b.kind === 'installment') {
    return (
      installmentBillingSortIndex(a.installmentId)
      - installmentBillingSortIndex(b.installmentId)
    );
  }
  if (a.kind === 'installment') return -1;
  if (b.kind === 'installment') return 1;
  const at = a.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bt = b.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
  if (at !== bt) return at - bt;
  return a.key.localeCompare(b.key);
}

/**
 * Derive unbilled installments + immediate change orders across progress-billing
 * projects. Rows already invoiced in QB are excluded; tracked items attach for
 * workflow status updates.
 */
export function buildReadyToBillQueue(inputs: ReadyToBillInputs): ReadyToBillRow[] {
  const now = inputs.now ?? new Date();
  const qbIndexes = buildQbInvoiceIndexes(inputs.qbInvoices);
  const dealsById = new Map(inputs.deals.filter((d) => d.id).map((d) => [d.id!, d] as const));
  const scheduleTasks = inputs.projectTasks ?? [];
  const scheduleGroups = inputs.taskGroups ?? [];
  const projectForDeal = (dealId: string) =>
    pickTimberProjectForDealSchedule(
      inputs.timberProjects,
      dealId,
      scheduleTasks,
      scheduleGroups,
    );
  const milestonesByProject = new Map<string, MilestoneLike[]>();
  for (const m of inputs.milestones) {
    if (!m.projectId) continue;
    const list = milestonesByProject.get(m.projectId) ?? [];
    list.push(m);
    milestonesByProject.set(m.projectId, list);
  }
  const changeOrdersByDeal = new Map<string, ChangeOrder[]>();
  for (const co of inputs.changeOrders) {
    if (!co.dealId) continue;
    const list = changeOrdersByDeal.get(co.dealId) ?? [];
    list.push(co);
    changeOrdersByDeal.set(co.dealId, list);
  }
  const coSalesById = new Map(
    (inputs.coSalesOrders ?? []).map((so) => [so.id, so] as const),
  );

  const trackedInstallmentByKey = new Map<string, BillingReadyItem>(
    inputs.trackedItems
      .filter((t) => t.kind === 'installment' && t.installmentId)
      .map((t) => [`${t.dealId}:${t.installmentId}`, t]),
  );
  const trackedCoByKey = new Map<string, BillingReadyItem>();
  for (const t of inputs.trackedItems) {
    if (t.kind !== 'change_order') continue;
    const coId = parseCoIdFromTrackedItem(t);
    if (coId) trackedCoByKey.set(coTrackedKey(t.dealId, coId), t);
  }

  const rows: ReadyToBillRow[] = [];

  // Deal-level "paid in full via legacy lump-sum invoice" — checked across ALL
  // base sales orders because old deals sometimes carry duplicates, and only
  // one of them links the invoice that was actually paid.
  const legacyFullPaidDeals = new Set<string>();
  for (const so of inputs.salesOrders) {
    if (so.isChangeOrder || !so.dealId) continue;
    const inv = so.quickbooksInvoiceId ? qbIndexes.byId.get(so.quickbooksInvoiceId) : undefined;
    if (
      legacyFullPaymentSatisfiedFromSalesOrder(so, {
        qbInvoiceTotal: inv?.totalAmount,
        qbInvoicePaid: isQbInvoicePaid(inv),
      })
    ) {
      legacyFullPaidDeals.add(so.dealId);
    }
  }

  // A QB invoice link is "dead" when the invoice was deleted or voided in
  // QuickBooks — it must not satisfy the legacy deposit or fabricate AR rows.
  const isDeadQbLink = (invoiceId?: string): boolean => {
    if (!invoiceId) return false;
    const status = qbIndexes.byId.get(invoiceId)?.status;
    return status === 'removed_in_qb' || status === 'voided';
  };

  for (const rawSo of pickPrimaryBaseSalesOrders(inputs.salesOrders)) {
    const so = isDeadQbLink(rawSo.quickbooksInvoiceId)
      ? { ...rawSo, quickbooksInvoiceId: undefined }
      : rawSo;
    const dealId = so.dealId;
    if (!dealId) continue;

    const legacyFullPaid = legacyFullPaidDeals.has(dealId);
    if (!inputs.forProjectSummary && legacyFullPaid) {
      continue;
    }

    if (!isEffectiveProgressBilling(so)) continue;

    const deal = dealsById.get(dealId);
    const project = projectForDeal(dealId);
    const milestones = project?.id ? (milestonesByProject.get(project.id) ?? []) : [];
    const dealCos = changeOrdersByDeal.get(dealId) ?? [];

    const legacyDeposit = legacyDepositSatisfiedFromSalesOrder(so);
    const billedIds = effectiveBilledInstallmentIds(so);

    const projectDates = {
      contractSignedAt: project?.contractedAt ?? so.createdAt,
      startDate: project?.startDate,
      targetEndDate: project?.targetEndDate,
    };

    const taskAnchors =
      project?.id
        ? resolvePaymentDueTaskAnchors(project.id, scheduleTasks, scheduleGroups)
        : {};
    const lastProductionTaskEnd = taskAnchors.production?.date ?? null;
    const lastProjectTaskEnd = taskAnchors.project?.date ?? null;

    const passed = new Set(
      computePassedInstallments(
        projectDates,
        milestones,
        now,
        lastProductionTaskEnd,
        taskAnchors.install?.date ?? taskAnchors.namedFinal?.date ?? null,
      ),
    );
    // Deposit stays passed. Invoice dates lock only change orders approved
    // after that bill, so a later credit reduces the still-open installment.
    const schedule = buildPaymentScheduleBreakdown(so.grandTotal ?? 0, dealCos, {
      invoicedInstallments: invoicedInstallmentLocks(so.installmentInvoices),
    });

    const changeOrderApprovedAt: Record<string, Date | string | number> = {};
    for (const co of dealCos) {
      if (co.id && co.approvedAt) {
        changeOrderApprovedAt[co.id] = co.approvedAt;
      }
    }

    const forecast = computePaymentDueForecast(
      projectDates,
      milestones,
      schedule,
      changeOrderApprovedAt,
      lastProductionTaskEnd,
      inputs.paymentLagSettings,
      lastProjectTaskEnd,
      {
        production: taskAnchors.production?.taskTitle,
        project: taskAnchors.project?.taskTitle,
        install: taskAnchors.install?.taskTitle,
      },
      taskAnchors,
    );
    const scheduleById = new Map(schedule.rows.map((r) => [r.id, r]));

    for (const flag of forecast) {
      if (flag.kind !== 'installment') continue;
      const installmentId = flag.installmentId as PaymentInstallmentId;
      const isStandardProgressInstallment =
        installmentId === 'deposit'
        || installmentId === 'pre_shipment'
        || installmentId === 'final';
      if (
        flag.amount <= 0
        && !billedIds.has(installmentId)
        && !(inputs.forProjectSummary && isStandardProgressInstallment)
      ) {
        continue;
      }

      const key = `${dealId}:${installmentId}`;
      const schedRow = scheduleById.get(installmentId);
      const adjNote = schedRow?.coAdjustments?.length
        ? formatCoAdjustmentsNote(schedRow.coAdjustments)
        : '';
      let detail = adjNote ? `${flag.detail} (${adjNote})` : flag.detail;
      const bundledCoCount = (inputs.coSalesOrders ?? []).filter(
        (coSo) => coSo.dealId === dealId && coSo.bundledOnInstallmentInvoice === installmentId,
      ).length;
      if (bundledCoCount > 0) {
        detail = `${detail} — includes ${bundledCoCount} bundled change order${bundledCoCount === 1 ? '' : 's'}`;
      }

      const milestoneEntries = (so.installmentInvoices ?? []).filter(
        (entry) => entry.installmentId === installmentId
          && entry.status !== 'voided'
          && entry.status !== 'removed_in_qb',
      );
      const hasPartial = milestoneEntries.some(
        (entry) => !!entry.partialId || (entry.structureIds?.length ?? 0) > 0,
      );
      const trackedForSlice = (invoiceNum?: string) => inputs.trackedItems.find((item) => (
        item.kind === 'installment'
        && item.dealId === dealId
        && item.installmentId === installmentId
        && (invoiceNum
          ? item.quickbooksInvoiceNum === invoiceNum
          : !item.quickbooksInvoiceNum && item.status === 'ready')
      ));

      if (hasPartial) {
        const groupsNote = (entry: typeof milestoneEntries[number]) => {
          const groups = (entry.structureGroupLabels || []).filter(Boolean);
          return groups.length ? `${groups.join(', ')} — ` : '';
        };
        for (const entry of milestoneEntries) {
          const invoiceId = entry.quickbooksInvoiceId;
          const qbFlags = getQbPaymentFlags(invoiceId, qbIndexes, entry);
          if (legacyFullPaid) qbFlags.qbPaid = true;
          const created = toDateSafe(entry.createdAt as unknown as Date);
          rows.push({
            key: `${dealId}:${installmentId}:${invoiceId || entry.partialId || 'slice'}`,
            kind: 'installment',
            dealId,
            salesOrderId: so.id,
            installmentId,
            dealLabel: moneyInDealLabel(deal, so, project, dealId),
            projectId: project?.id,
            anchorTaskId: flag.anchorTaskId,
            label: flag.label,
            detail: `${groupsNote(entry)}${detail}`,
            amount: Number(entry.amount) || 0,
            scheduleAmount: flag.amount,
            dueDate: created || now,
            dueAnchor: 'Due on receipt',
            estimated: false,
            billableNow: false,
            tracked: trackedForSlice(entry.quickbooksInvoiceNum),
            qbPaid: qbFlags.qbPaid,
            qbInvoiced: qbFlags.qbPaid ? undefined : (qbFlags.qbInvoiced ?? !!invoiceId),
            quickbooksInvoiceNum: entry.quickbooksInvoiceNum
              ?? findQbInvoice(invoiceId, entry.quickbooksInvoiceNum, qbIndexes)?.invoiceNumber,
            quickbooksInvoiceId: invoiceId,
          });
        }
        const closed = milestoneEntries.some((entry) => invoiceClosesMilestone(entry));
        const billed = milestoneEntries.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
        const remaining = Math.max(0, Number(flag.amount) - billed);
        if (!closed && remaining > 0) {
          rows.push({
            key,
            kind: 'installment',
            dealId,
            salesOrderId: so.id,
            installmentId,
            dealLabel: moneyInDealLabel(deal, so, project, dealId),
            projectId: project?.id,
            anchorTaskId: flag.anchorTaskId,
            label: flag.label,
            detail: `${detail} — still to bill`,
            amount: remaining,
            scheduleAmount: flag.amount,
            dueDate: flag.dueDate,
            dueAnchor: describeReceivableDueAnchor(flag),
            estimated: flag.estimated,
            billableNow:
              installmentId === 'deposit'
                ? true
                : passed.has(installmentId) || (flag.dueDate != null && flag.dueDate.getTime() <= now.getTime()),
            tracked: trackedForSlice(),
            qbPaid: false,
            qbInvoiced: false,
          });
        }
        continue;
      }

      const installmentEntry = milestoneEntries[0];
      const invoiceId =
        installmentEntry?.quickbooksInvoiceId
        ?? (installmentId === 'deposit' && legacyDeposit ? so.quickbooksInvoiceId : undefined);
      const installmentStatus =
        installmentEntry?.status
        ?? (installmentId === 'deposit' && legacyDeposit ? 'paid' : undefined);
      const qbFlags = getQbPaymentFlags(
        invoiceId,
        qbIndexes,
        installmentEntry
          ? installmentEntry
          : installmentStatus
            ? { status: installmentStatus }
            : undefined,
      );
      // Whole contract settled by one legacy lump-sum invoice — every
      // installment is money already received, not open AR.
      if (legacyFullPaid) qbFlags.qbPaid = true;

      const tracked = trackedInstallmentByKey.get(key);
      if (tracked?.status === 'paid') qbFlags.qbPaid = true;

      const isBilled = billedIds.has(installmentId);
      if (isBilled) {
        rows.push({
          key,
          kind: 'installment',
          dealId,
          salesOrderId: so.id,
          installmentId,
          dealLabel: moneyInDealLabel(deal, so, project, dealId),
          projectId: project?.id,
          anchorTaskId: flag.anchorTaskId,
          label: flag.label,
          detail,
          amount: installmentEntry?.amount ?? flag.amount,
          dueDate: flag.dueDate,
          dueAnchor: describeReceivableDueAnchor(flag),
          estimated: flag.estimated,
          billableNow: false,
          tracked,
          qbPaid: qbFlags.qbPaid,
          qbInvoiced: qbFlags.qbPaid ? undefined : (qbFlags.qbInvoiced ?? !!invoiceId),
          quickbooksInvoiceNum:
            installmentEntry?.quickbooksInvoiceNum
            ?? findQbInvoice(invoiceId, installmentEntry?.quickbooksInvoiceNum, qbIndexes)
              ?.invoiceNumber,
        });
        continue;
      }

      const billableNow =
        installmentId === 'deposit'
          ? true
          : passed.has(installmentId) ||
            (flag.dueDate != null && flag.dueDate.getTime() <= now.getTime());

      rows.push({
        key,
        kind: 'installment',
        dealId,
        salesOrderId: so.id,
        installmentId,
        dealLabel: moneyInDealLabel(deal, so, project, dealId),
        projectId: project?.id,
        anchorTaskId: flag.anchorTaskId,
        label: flag.label,
        detail,
        amount: flag.amount,
        dueDate: flag.dueDate,
        dueAnchor: describeReceivableDueAnchor(flag),
        estimated: flag.estimated,
        billableNow,
        tracked,
        qbPaid: qbFlags.qbPaid,
        qbInvoiced: qbFlags.qbPaid ? undefined : qbFlags.qbInvoiced,
      });
    }
  }

  // Immediate change orders — separate invoice per approved CO (unless bundled on a progress installment).
  for (const co of inputs.changeOrders) {
    if (!co.id || !co.dealId || co.status !== 'approved') continue;
    if (!changeOrderBillsImmediately(co)) continue;
    if ((Number(co.deltaAmount) || 0) <= 0) continue;

    const coSoId = co.salesOrderId ?? `co-${co.id}`;
    const coMatches = matchingChangeOrderSalesOrders(co, inputs.coSalesOrders ?? []);
    const { preferred: coSoFromMatches, qbPaid: siblingPaid, qbInvoiced: siblingInvoiced } =
      preferPaidChangeOrderSalesOrder(coMatches, qbIndexes);
    const coSo = coSoFromMatches ?? coSalesById.get(coSoId);
    if (coSo?.bundledOnInstallmentInvoice) continue;

    const key = coTrackedKey(co.dealId, co.id);
    const tracked = trackedCoByKey.get(key);
    const coQbFlags = salesOrderQbFlags(coSo, qbIndexes);
    if (siblingPaid) coQbFlags.qbPaid = true;
    else if (siblingInvoiced) coQbFlags.qbInvoiced = true;
    if (tracked?.status === 'paid') coQbFlags.qbPaid = true;

    const deal = dealsById.get(co.dealId);
    const project = projectForDeal(co.dealId);
    const ref = coDisplayReference(co, deal);
    rows.push({
      key,
      kind: 'change_order',
      dealId: co.dealId,
      salesOrderId: coSo?.id ?? coSoId,
      changeOrderId: co.id,
      coReference: ref,
      dealLabel: moneyInDealLabel(deal, coSo ?? {}, project, co.dealId),
      projectId: project?.id,
      label: ref,
      detail: co.title ? `${co.title} — due upon approval` : 'Due upon approval',
      amount: Number(co.deltaAmount) || 0,
      dueDate: toDateSafe(co.approvedAt),
      dueAnchor: co.approvedAt ? 'due upon approval' : 'approval date not set',
      estimated: !co.approvedAt,
      billableNow: !!co.approvedAt,
      tracked,
      qbPaid: coQbFlags.qbPaid,
      qbInvoiced: coQbFlags.qbInvoiced,
    });
  }

  rows.sort((a, b) => {
    if (inputs.forProjectSummary) {
      return compareReadyToBillBillingChronology(a, b);
    }
    if (a.billableNow !== b.billableNow) return a.billableNow ? -1 : 1;
    const at = a.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bt = b.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return at - bt;
  });
  return rows;
}
